import { CalendarDays, ChevronDown } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

export interface DateRangeValue {
    from: string
    to: string
}

export interface DateRangeFilterProps {
    label?: string
    value: DateRangeValue
    onChange: (next: DateRangeValue) => void
    className?: string
}

/** Local calendar day, `YYYY-MM-DD`, for the `max`/`value` of a date input. */
function today(): string {
    const now = new Date()
    const offset = now.getTimezoneOffset() * 60_000
    return new Date(now.getTime() - offset).toISOString().slice(0, 10)
}

function shiftDays(days: number): string {
    const now = new Date()
    now.setDate(now.getDate() - days)
    const offset = now.getTimezoneOffset() * 60_000
    return new Date(now.getTime() - offset).toISOString().slice(0, 10)
}

/** The ranges an operator actually asks for, so the common case is one click. */
const PRESETS: { label: string; days: number }[] = [
    { label: 'Today', days: 0 },
    { label: 'Last 7 days', days: 6 },
    { label: 'Last 30 days', days: 29 },
    { label: 'Last 90 days', days: 89 }
]

export function DateRangeFilter({ label = 'Date', value, onChange, className }: DateRangeFilterProps) {
    const [open, setOpen] = useState(false)
    const active = value.from !== '' || value.to !== ''
    const max = today()

    const summary = active ? [value.from || 'any', value.to || 'now'].join(' \u2192 ') : null

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    aria-label={`${label} range filter`}
                    // Cluster, not an isolated control: grown on a coarse pointer to
                    // match the other filter triggers instead of hit-slopped.
                    className={cn('justify-between gap-1.5 pointer-coarse:h-10', className)}>
                    <CalendarDays aria-hidden className="size-3.5 text-muted-foreground" />
                    <span className={active ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
                    {summary && (
                        <>
                            <Separator orientation="vertical" className="h-3.5" />
                            <span className="tabular text-foreground">{summary}</span>
                        </>
                    )}
                    <ChevronDown aria-hidden className="size-3.5 text-subtle-foreground" />
                </Button>
            </PopoverTrigger>

            <PopoverContent align="start" className="w-64">
                <div className="flex flex-col gap-3">
                    {/* A four-up grid of 28px chips is a cluster: grow the chips and the
                        gap together on touch. Two 108px columns still fit the w-64 popover. */}
                    <div className="grid grid-cols-2 gap-1.5 pointer-coarse:gap-2">
                        {PRESETS.map(preset => (
                            <Button
                                key={preset.label}
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    onChange({ from: shiftDays(preset.days), to: max })
                                    setOpen(false)
                                }}
                                className="pointer-coarse:h-10">
                                {preset.label}
                            </Button>
                        ))}
                    </div>

                    <Separator />

                    <div className="flex flex-col gap-2">
                        <div className="flex flex-col gap-1">
                            <Label htmlFor="date-range-from">From</Label>
                            <Input
                                id="date-range-from"
                                type="date"
                                value={value.from}
                                // An open-ended upper bound is fine, but `from` after
                                // `to` returns nothing and looks like a broken filter.
                                max={value.to || max}
                                onChange={event => onChange({ ...value, from: event.target.value })}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <Label htmlFor="date-range-to">To</Label>
                            <Input
                                id="date-range-to"
                                type="date"
                                value={value.to}
                                min={value.from || undefined}
                                max={max}
                                onChange={event => onChange({ ...value, to: event.target.value })}
                            />
                        </div>
                    </div>

                    {active && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onChange({ from: '', to: '' })}
                            className="pointer-coarse:h-10">
                            Clear
                        </Button>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    )
}
